import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Sayfa basligi ve aciklamasini adrese gore ayarlar.
//
// Onceden tum sayfalar index.html'deki tek basligi kullaniyordu
// ("Saraylı Moda by Sultan Asan"). Bunun iki sonucu vardi:
//   - Arama motorlarinda her sayfa ayni baslikla gorunuyordu
//   - Kullanici birden fazla sekme actiginda hangisi ne, ayirt edemiyordu
//
// Dinamik sayfalar (urun detayi gibi) kendi basligini setPageMeta ile
// ayarlar; bu bilesen yalnizca sabit rotalari yonetir.

const SITE_NAME = 'Saraylı Moda';

interface Meta {
  title: string;
  description: string;
}

const ROUTE_META: Record<string, Meta> = {
  '/': {
    title: 'Saraylı Moda by Sultan Asan — Gelinlik, Abiye ve Kınalık',
    description:
      'Saraylı Moda; el işçiliğiyle hazırlanan gelinlik, abiye ve kınalık koleksiyonları. Randevu alarak koleksiyonumuzu mağazamızda inceleyebilirsiniz.',
  },
  '/koleksiyonlar': {
    title: `Koleksiyonlar | ${SITE_NAME}`,
    description: 'Sezonun öne çıkan gelinlik, abiye ve kınalık koleksiyonlarını keşfedin.',
  },
  '/ürünlerimiz': {
    title: `Ürünlerimiz | ${SITE_NAME}`,
    description: 'Gelinlik, abiye, kınalık ve aksesuar ürünlerimizin tamamı.',
  },
  '/gelinlikler': {
    title: `Gelinlik Koleksiyonu | ${SITE_NAME}`,
    description:
      'A kesim, prenses ve balık model gelinlikler. El işçiliği nakış ve işleme detaylarıyla.',
  },
  '/abiyeler': {
    title: `Abiye Koleksiyonu | ${SITE_NAME}`,
    description: 'Nişan, düğün ve özel davetler için abiye elbise koleksiyonumuz.',
  },
  '/kinaliklar': {
    title: `Kınalık Koleksiyonu | ${SITE_NAME}`,
    description: 'Geleneksel ve modern tasarımlarıyla kınalık kaftan ve bindallı modelleri.',
  },
  '/aksesuar': {
    title: `Aksesuarlar | ${SITE_NAME}`,
    description: 'Duvak, taç, kemer ve gelin aksesuarları.',
  },
  '/hakkimizda': {
    title: `Hakkımızda | ${SITE_NAME}`,
    description: 'Saraylı Moda ve Sultan Asan hakkında.',
  },
  '/iletisim': {
    title: `İletişim | ${SITE_NAME}`,
    description: 'Mağaza adresimiz, telefon numaramız ve iletişim formumuz.',
  },
  '/randevu': {
    title: `Randevu Al | ${SITE_NAME}`,
    description:
      'Gelinlik provası ve koleksiyon incelemesi için mağazamızdan ücretsiz randevu alın.',
  },
  '/sepet': {
    title: `Sepetim | ${SITE_NAME}`,
    description: 'Sepetinizdeki ürünler.',
  },
  '/odeme': {
    title: `Ödeme | ${SITE_NAME}`,
    description: 'Siparişinizi güvenli ödeme altyapısıyla tamamlayın.',
  },
  '/siparis-takip': {
    title: `Sipariş Takip | ${SITE_NAME}`,
    description: 'Sipariş numaranızla siparişinizin durumunu sorgulayın.',
  },
  '/login': {
    title: `Giriş Yap | ${SITE_NAME}`,
    description: 'Hesabınıza giriş yapın.',
  },
  '/register': {
    title: `Kayıt Ol | ${SITE_NAME}`,
    description: 'Yeni hesap oluşturun.',
  },
  '/profil': {
    title: `Hesabım | ${SITE_NAME}`,
    description: 'Siparişleriniz ve kayıtlı adresleriniz.',
  },
  '/gizlilik': {
    title: `Gizlilik Politikası | ${SITE_NAME}`,
    description: 'Kişisel verilerinizin işlenmesine ilişkin aydınlatma metni.',
  },
  '/mesafeli-satis': {
    title: `Mesafeli Satış Sözleşmesi | ${SITE_NAME}`,
    description: 'Mesafeli satış sözleşmesi koşulları.',
  },
  '/teslimat-iade': {
    title: `Teslimat ve İade | ${SITE_NAME}`,
    description: 'Kargo, teslimat ve iade koşullarımız.',
  },
  '/arama': {
    title: `Ürün Arama | ${SITE_NAME}`,
    description: 'Gelinlik, abiye ve kınalık koleksiyonlarımızda arama yapın.',
  },
  '/admin': {
    title: `Yönetim Paneli | ${SITE_NAME}`,
    description: '',
  },
};

const FALLBACK: Meta = {
  title: `Sayfa Bulunamadı | ${SITE_NAME}`,
  description: '',
};

// Dinamik sayfalarin kendi basligini ayarlamasi icin
export function setPageMeta(title: string, description?: string) {
  document.title = title;
  if (description !== undefined) {
    applyDescription(description);
  }
}

function applyDescription(description: string) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', description);
}

function applyCanonical(pathname: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', `${window.location.origin}${pathname}`);
}

const PageMeta: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[pathname];

    // Urun detay sayfalari kendi basligini ayarlar; burada ezmiyoruz.
    const isProductDetail = /^\/(gelinlikler|abiyeler|kinaliklar|aksesuar)\/.+/.test(pathname);
    if (isProductDetail) {
      applyCanonical(pathname);
      return;
    }

    const applied = meta || FALLBACK;
    document.title = applied.title;
    applyDescription(applied.description);
    applyCanonical(pathname);
  }, [pathname]);

  return null;
};

export default PageMeta;
